'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { MessagesSquare } from 'lucide-react'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@elmnts/ui/hover-card'
import { Popover, PopoverContent, PopoverTrigger } from '@elmnts/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@elmnts/ui/tooltip'
import { ChannelAvatar } from '@/components/ChannelAvatar'
import { ChannelPresence } from '@/components/ChannelPresenceAvatars/ChannelPresence'
import { PhoneNumber } from '@/components/PhoneNumber'
import { useChannelIdentity } from '@/hooks/useChannelIdentity'
import { useChannelsQuery } from '@/hooks/useChannelsQuery'
import { describeChannelCounters, unreadBadgeLabel } from '@/lib/format/channelCounters'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthContext'
import { mergeBrandChannels, sumUnread } from './brandChannels'
import { useIsMobile } from '@/hooks/use-mobile'

export interface BrandMenuProps {
  /** Canal atual (derivado do pathname pelo `AppRail`) — destaca o item correspondente na lista. */
  currentChannelId: string | null
  /** Rota home ativa (`/chatbot`) — anel de destaque no quadrado da marca. */
  active?: boolean
}

const CLOSE_DELAY_MS = 150

/**
 * Abertura do hover aninhado. Mais alto que o do popover externo de propósito: o mouse
 * atravessa a lista inteira pra chegar no canal que interessa, e abrir a descrição em
 * cada linha de passagem é o piscar que esta tela precisa não ter. Fechar usa o mesmo
 * `CLOSE_DELAY_MS` do externo — os dois delays têm que conversar, senão o aninhado
 * sobrevive ao pai ou morre antes dele.
 */
const HOVER_OPEN_DELAY_MS = 300

/**
 * Quadrado rosa da marca = único ponto de entrada do topo do rail: clique navega pra home
 * (/chatbot); hover abre a identidade do número + a lista de canais quando o usuário tem MAIS
 * DE UM (clicar num canal navega direto). Consolida os antigos ícones de marca, Home e
 * ChannelSwitcher em um só.
 */
export function BrandMenu({ currentChannelId, active = false }: BrandMenuProps) {
  const router = useRouter()
  const isMobile = useIsMobile()
  const { user } = useAuth()
  // A lista HTTP é a única que traz `icon`/`color`, mas fica `undefined` até a rede
  // resolver (`useChannelsQuery.md`). O snapshot do auth é síncrono e sobrevive à falha
  // do `/auth/me` — perder o ícone é degradação aceitável, perder a troca de canal não.
  const { data } = useChannelsQuery()
  const identity = useChannelIdentity(currentChannelId)
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const toqueEmAndamento = useRef(false)

  const channels = useMemo(() => mergeBrandChannels(user?.channels, data), [user?.channels, data])
  const hasMenu = channels.length > 1
  // O badge NUNCA é opcional: ele é estado, não notificação. A preferência de
  // notificação (`notif_prefs_v1.channelActivity`) governa só o toast.
  const unreadTotal = sumUnread(channels)

  const { photoSrc, phone } = identity
  // `||` e NUNCA `??`: o back pode mandar `verified_name: ''` e `??` renderizaria rótulo vazio.
  const verifiedName = phone?.verifiedName || ''
  const displayPhoneNumber = phone?.displayPhoneNumber || ''
  const hasPhoneHeader = Boolean(photoSrc || verifiedName || displayPhoneNumber)

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  // Delay no fechamento pra permitir mover o mouse do trigger até o conteúdo do popover.
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS)
  }

  useEffect(() => cancelClose, [])

  const handleSelect = (dbId: string) => {
    cancelClose()
    setOpen(false)
    router.push(`/chatbot/${dbId}`)
  }

  const trigger = (
    <Link
      href="/chatbot"
      aria-label={unreadTotal > 0 ? `Home (${unreadTotal} mensagens não lidas)` : 'Home'}
      data-active={active || undefined}
      onPointerDown={(event) => {
        toqueEmAndamento.current = event.pointerType !== 'mouse'
      }}
      onPointerEnter={(event) => {
        cancelClose()
        if (event.pointerType === 'mouse' && hasMenu && !isMobile) setOpen(true)
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') scheduleClose()
      }}
      onClick={(event) => {
        cancelClose()
        setOpen(false)
        if (isMobile || toqueEmAndamento.current) {
          event.preventDefault()
          router.push('/chatbot')
        }
        toqueEmAndamento.current = false
      }}
      className="relative flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground transition-all hover:opacity-90 data-[active=true]:ring-2 data-[active=true]:ring-sidebar-ring data-[active=true]:ring-offset-2 data-[active=true]:ring-offset-sidebar"
    >
      {photoSrc ? (
        <Image
          src={photoSrc}
          alt=""
          width={32}
          height={32}
          unoptimized
          className="size-8 rounded-lg object-cover"
        />
      ) : (
        <MessagesSquare className="size-4" />
      )}
      {unreadTotal > 0 && (
        <span
          data-slot="unread-badge"
          aria-hidden="true"
          className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white"
        >
          {unreadBadgeLabel(unreadTotal)}
        </span>
      )}
    </Link>
  )

  // No touch, the marca é navegação direta para Home. Não montar o trigger do
  // Popover evita que o primeiro toque abra apenas o hover e deixe o canal ativo.
  if (isMobile) return trigger

  if (!hasMenu) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="right">{verifiedName || 'Home'}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        className="w-64 p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        {hasPhoneHeader && (
          <>
            <div className="flex items-center gap-2.5 px-2 py-1.5">
              {photoSrc && (
                <Image
                  src={photoSrc}
                  alt=""
                  width={32}
                  height={32}
                  unoptimized
                  className="size-8 shrink-0 rounded-lg object-cover"
                />
              )}
              <div className="flex min-w-0 flex-col">
                {verifiedName && (
                  <span className="truncate text-sm font-medium text-foreground">
                    {verifiedName}
                  </span>
                )}
                {displayPhoneNumber && (
                  <PhoneNumber
                    phone={displayPhoneNumber}
                    className="text-xs text-muted-foreground"
                  />
                )}
              </div>
            </div>

            <div className="my-1 h-px bg-border" />
          </>
        )}

        <div className="px-2 py-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Canais
          </span>
        </div>

        <div className="flex flex-col gap-0.5">
          {channels.map((channel) => (
            <HoverCard
              key={channel.db_id}
              openDelay={HOVER_OPEN_DELAY_MS}
              closeDelay={CLOSE_DELAY_MS}
            >
              <HoverCardTrigger asChild>
                <button
                  type="button"
                  onClick={() => handleSelect(channel.db_id)}
                  data-active={channel.db_id === currentChannelId || undefined}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    // Destaque sutil: preencher a linha com `bg-primary` brigaria com a cor
                    // do próprio ChannelAvatar, que é o que identifica o canal aqui.
                    'data-[active=true]:bg-primary/10 data-[active=true]:font-medium',
                  )}
                >
                  <ChannelAvatar size="sm" icon={channel.icon} color={channel.color} />
                  <span className="flex-1 truncate">{channel.name}</span>
                  {/* Quem está no canal, ANTES do badge. Só hooks app-wide: o BrandMenu vive
                      fora do ChatBotProvider. */}
                  <ChannelPresence channelId={channel.db_id} size="xs" />
                  {channel.total_unread_messages > 0 && (
                    <span
                      data-slot="unread-badge"
                      className="ml-auto flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none tabular-nums text-white"
                    >
                      {unreadBadgeLabel(channel.total_unread_messages)}
                    </span>
                  )}
                </button>
              </HoverCardTrigger>
              {/*
                O content é PORTALADO pra fora do `PopoverContent`, então o mouse indo
                da linha até ele dispara o `onMouseLeave` do externo e agenda o
                fechamento da lista — com o hover JÁ aberto. `cancelClose` no enter
                desarma; `scheduleClose` no leave devolve o comportamento normal.
              */}
              <HoverCardContent
                side="right"
                align="start"
                className="w-auto max-w-72 p-2 text-xs text-muted-foreground"
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
              >
                {describeChannelCounters(channel)}
              </HoverCardContent>
            </HoverCard>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
